import React, { useEffect, useState } from "react";
import { Page } from "@/components/PageConsole";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Plus, Trash2, Droplets, Printer } from "@/components/icons";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import { plural } from "@/lib/plural";
import {
  StatusBadge,
  OwnershipBadge,
  LOCATION_TYPE_LABELS,
  ReplacementBadge,
  AssetStatusBadge,
  formatEuro,
} from "@/components/Badges";
import { DemarcationEditor } from "@/pages/SettingsDemarcation";
import { InspectionsPanel } from "@/components/InspectionsPanel";
import { InlineEmpty, ErrorState } from "@/components/ListState";
import { PuntenPanel } from "@/components/PuntenPanel";
import { RuimtesPanel } from "@/components/RuimtesPanel";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const Field = ({ label, value }) => {
  if (value === null || value === undefined || value === "") return null;
  return (
    <div>
      <div className="text-data uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-body text-foreground">{value}</div>
    </div>
  );
};

export default function LocationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite, canView } = useAuth();
  const [loc, setLoc] = useState(null);
  const [entityName, setEntityName] = useState(null);
  const [categories, setCategories] = useState([]);
  const [assets, setAssets] = useState([]);
  const [ruimtes, setRuimtes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [savingOverrides, setSavingOverrides] = useState(false);
  const [newOverride, setNewOverride] = useState({ category_id: "", lifetime_months: "" });
  const [demRows, setDemRows] = useState([]);
  const [savingDem, setSavingDem] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [summary, setSummary] = useState(null);

  const loadLocation = async () => {
    const { data } = await api.get(`/locations/${id}`);
    setLoc(data);
    setDemRows(data.demarcation || []);
    return data;
  };

  const loadRuimtes = async () => {
    const { data } = await api.get(`/ruimtes?location_id=${id}`);
    setRuimtes(data);
    return data;
  };

  // A spoor rename updates the machines server-side, so the table below must be
  // re-read too: showing the old label under the new name is a lie about which
  // spoor a machine stands on.
  const reloadPlaces = async () => {
    const [rui, allAssets] = await Promise.all([loadRuimtes(), api.get("/assets")]);
    setAssets(allAssets.data.filter((a) => a.location_id === id));
    return rui;
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const data = await loadLocation();
        const [cats, allAssets, ents] = await Promise.all([
          api.get("/asset-categories"),
          api.get("/assets"),
          api.get("/entities"),
          loadRuimtes(),
        ]);
        setCategories(cats.data);
        setAssets(allAssets.data.filter((a) => a.location_id === id));
        // Repair/recoverable totals live behind the 'verhaalbaar' module.
        if (canView("verhaalbaar")) {
          try { const sum = await api.get(`/locations/${id}/repair-summary`); setSummary(sum.data); }
          catch (e) { /* ignore */ }
        }
        if (data.entity) {
          const match = ents.data.find((e) => e.entity_id === data.entity);
          setEntityName(match ? match.name : data.entity);
        } else {
          setEntityName(null);
        }
      } catch (e) {
        setError(getErrorMessage(e, "Locatie niet gevonden."));
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  // Machines grouped by the ruimte they stand in, ruimtes in their own order,
  // and whatever has no place yet last -- named, not hidden.
  const assetGroups = React.useMemo(() => {
    const order = ruimtes.map((r) => r.id);
    const byRuimte = new Map();
    assets.forEach((a) => {
      const key = a.ruimte_id && order.includes(a.ruimte_id) ? a.ruimte_id : "__none__";
      if (!byRuimte.has(key)) byRuimte.set(key, []);
      byRuimte.get(key).push(a);
    });
    const sortItems = (list) => list.slice().sort((x, y) =>
      String(x.spoor || "").localeCompare(String(y.spoor || "")) ||
      String(x.asset_code || "").localeCompare(String(y.asset_code || "")));
    const groups = ruimtes
      .filter((r) => byRuimte.has(r.id))
      .map((r) => ({ key: r.id, title: r.name, items: sortItems(byRuimte.get(r.id)) }));
    if (byRuimte.has("__none__")) {
      groups.push({ key: "__none__", title: "Nog niet ingedeeld", items: sortItems(byRuimte.get("__none__")) });
    }
    return groups;
  }, [assets, ruimtes]);

  // Sporen only exist inside a was-hal, so a restaurant, kantoor or opslag has
  // no Spoor column and no "0 sporen" opening fact: a zero counter is not shown.
  const sporenTotal = ruimtes.reduce((n, r) => n + (r.sporen?.length || 0), 0);
  const catName = (cid) => categories.find((c) => c.category_id === cid)?.name || cid;  const catDefault = (cid) => categories.find((c) => c.category_id === cid)?.default_lifetime_months;

  const saveOverrides = async (overrides) => {
    setSavingOverrides(true);
    try {
      await api.put(`/locations/${id}`, { category_lifetime_overrides: overrides });
      await loadLocation();
      toast.success("Afwijkende levensduur opgeslagen");
    } catch (e) {
      toast.error(getErrorMessage(e, "Opslaan mislukt."));
    } finally {
      setSavingOverrides(false);
    }
  };

  const addOverride = () => {
    if (!newOverride.category_id || newOverride.lifetime_months === "") {
      toast.error("Kies een categorie en vul een levensduur in.");
      return;
    }
    // 0 maanden zou elke machine in die categorie voor altijd op "OVER" zetten.
    const months = Number(newOverride.lifetime_months);
    if (!Number.isFinite(months) || months < 1) {
      toast.error("Een levensduur is minimaal 1 maand.");
      return;
    }
    const others = (loc.category_lifetime_overrides || []).filter(
      (o) => o.category_id !== newOverride.category_id
    );
    const next = [...others, { category_id: newOverride.category_id, lifetime_months: months }];
    setNewOverride({ category_id: "", lifetime_months: "" });
    saveOverrides(next);
  };

  const removeOverride = (cid) => {
    const next = (loc.category_lifetime_overrides || []).filter((o) => o.category_id !== cid);
    saveOverrides(next);
  };

  const saveDemarcation = async () => {
    setSavingDem(true);
    try {
      await api.put(`/locations/${id}`, { demarcation: demRows });
      await loadLocation();
      toast.success("Wie betaalt wat opgeslagen");
    } catch (e) {
      toast.error(getErrorMessage(e, "Opslaan mislukt."));
    } finally {
      setSavingDem(false);
    }
  };

  const applyTemplate = async () => {
    setApplyOpen(false);
    setSavingDem(true);
    try {
      await api.post(`/locations/${id}/demarcation/apply-template`);
      await loadLocation();
      toast.success("Sjabloon toegepast op deze locatie");
    } catch (e) {
      toast.error(getErrorMessage(e, "Toepassen mislukt."));
    } finally {
      setSavingDem(false);
    }
  };

  const downloadStickers = async () => {
    try {
      const res = await api.get(`/locations/${id}/qr-stickers.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `qr-stickers-${id}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(getErrorMessage(e, "PDF genereren mislukt."));
    }
  };

  if (loading)
    return (
      <Page console={{ variant: "view", title: "Locatie" }}>
        <div className="space-y-4" data-testid="location-detail-loading">
          <Card className="p-5"><Skeleton className="h-40 w-full" /></Card>
        </div>
      </Page>
    );
  if (error || !loc)
    return (
      <Page console={{ variant: "view", title: "Locatie" }}>
        <ErrorState
          testid="location-load-error"
          message={error || "Locatie niet gevonden."}
          onRetry={loadLocation}
        />
      </Page>
    );

  const consoleActions = [
    // Order matters for the phone bottom bar only as a fallback; the primary is
    // chosen by variant. Bewerken first so both mechanisms agree.
    ...(canWrite() ? [{ key: "bewerken", label: "Bewerken", icon: Pencil, variant: "primary",
        testid: "location-edit-button", onClick: () => navigate(`/locaties/${loc.location_id}/bewerken`) }] : []),
    { key: "stickers", label: "QR-stickers (PDF)", icon: Printer, testid: "location-qr-stickers-button", onClick: downloadStickers },
    { key: "terug", label: "Alle locaties", icon: ArrowLeft, onClick: () => navigate("/locaties") },
  ];

  return (
    <Page console={{ variant: "view", title: loc.name, actions: consoleActions,
      facts: [...(sporenTotal > 0 ? [plural(sporenTotal, "spoor", "sporen")] : []),
        loc.ownership === "huur" ? "gehuurd" : "eigendom",
        ...(loc.address?.city && loc.address.city !== loc.name ? [loc.address.city] : [])] }}>
      <div className="space-y-4">
      <Card className="p-5">
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <StatusBadge status={loc.status} />
          <OwnershipBadge ownership={loc.ownership} />
          <span className="rounded border border-border bg-muted px-2 py-0.5 text-label font-semibold uppercase tracking-[0.07em] text-foreground">
            {LOCATION_TYPE_LABELS[loc.type]}
          </span>
          {loc.water_recycling && (
            <span className="inline-flex items-center gap-1 rounded border border-wacht-rand bg-wacht-vlak px-2 py-0.5 text-label font-semibold uppercase tracking-[0.07em] text-wacht-tekst">
              <Droplets size={14} /> Waterrecycling
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          <Field label="Adres" value={loc.address?.street} />
          <Field label="Postcode" value={loc.address?.postal_code} />
          <Field label="Plaats" value={loc.address?.city !== loc.name ? loc.address?.city : ""} />
          <Field label="Entiteit" value={entityName} />
          {loc.note && <Field label="Notitie" value={loc.note} />}
        </div>
      </Card>

      {canView("punten") && <PuntenPanel locationId={loc.location_id} locationName={loc.name} />}

      <RuimtesPanel
        locationId={loc.location_id}
        locationType={loc.type}
        ruimtes={ruimtes}
        assets={assets}
        writable={canWrite()}
        onChanged={reloadPlaces}
      />

      <Card className="p-5">
        <h2 className="mb-3 text-body font-semibold text-foreground">Verhuurder</h2>
        {loc.ownership === "huur" ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Field label="Naam" value={loc.landlord?.name} />
            <Field label="Contactpersoon" value={loc.landlord?.contact} />
            <Field label="E-mail" value={loc.landlord?.email} />
            <Field label="Telefoon" value={loc.landlord?.phone} />
          </div>
        ) : (
          <p className="text-body text-muted-foreground">
            Deze locatie is eigendom - geen verhuurdergegevens.
          </p>
        )}
      </Card>

      {loc.ownership === "huur" && (
        <Card className="p-5" data-testid="location-demarcation-card">
          <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
            <div>
              <h2 className="text-body font-semibold text-foreground">Wie betaalt wat (huur)</h2>
              <p className="text-data text-muted-foreground">
                Wie is verantwoordelijk per bouwdeel: wij of de verhuurder. Pas dit aan op basis van het huurcontract van deze locatie.
              </p>
            </div>
            {canWrite() && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                data-testid="demarcation-apply-template-button"
                disabled={savingDem}
                onClick={() => setApplyOpen(true)}
              >
                Sjabloon toepassen
              </Button>
            )}
          </div>
          <DemarcationEditor rows={demRows} onChange={setDemRows} writable={canWrite()} />
          {canWrite() && (
            <div className="mt-4">
              <Button data-testid="demarcation-location-save-button" disabled={savingDem} onClick={saveDemarcation}>
                {savingDem ? "Bezig..." : "Wie betaalt wat opslaan"}
              </Button>
            </div>
          )}

          {summary && (
            <>
              <Separator className="my-5" />
              <h3 className="mb-3 text-body font-semibold text-foreground">Reparatiekosten (laatste 12 maanden)</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="rounded-md border border-border bg-muted/30 p-4">
                  <div className="text-data uppercase tracking-wide text-muted-foreground">Totaal reparatiekosten</div>
                  <div className="mt-1 text-kop font-semibold tabular-nums text-foreground" data-testid="location-repair-total-12m">
                    {formatEuro(summary.total_12m)}
                  </div>
                  {summary.tarief_ontbreekt_count > 0 && (
                    <div className="mt-1 text-data text-letop-tekst" data-testid="location-tarief-ontbreekt-12m">
                      + {summary.tarief_ontbreekt_count} {summary.tarief_ontbreekt_count === 1 ? "bon" : "bonnen"}
                      {summary.tarief_ontbreekt_uren > 0 ? ` (${String(summary.tarief_ontbreekt_uren).replace(".", ",")} uur)` : ""}
                      {" "}zonder tarief
                    </div>
                  )}
                </div>
                <div className="rounded-md border border-letop-rand bg-letop-vlak p-4">
                  <div className="text-data uppercase tracking-wide text-letop-tekst">Mogelijk verhaalbaar op verhuurder</div>
                  <div className="mt-1 text-kop font-semibold tabular-nums text-letop-tekst" data-testid="location-recoverable-12m">
                    {formatEuro(summary.recoverable_12m)}
                  </div>
                  {summary.tarief_ontbreekt_recoverable_count > 0 && (
                    <div className="mt-1 text-data text-letop-tekst" data-testid="location-tarief-ontbreekt-recoverable">
                      + {summary.tarief_ontbreekt_recoverable_count} verhaalbare {summary.tarief_ontbreekt_recoverable_count === 1 ? "bon" : "bonnen"} zonder tarief
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          <AlertDialog open={applyOpen} onOpenChange={setApplyOpen}>
            <AlertDialogContent data-testid="demarcation-apply-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Sjabloon toepassen?</AlertDialogTitle>
                <AlertDialogDescription>
                  Dit vervangt de huidige "wie betaalt wat" van deze locatie
                  {(loc.demarcation || []).length > 0
                    ? ` (${plural((loc.demarcation || []).length, "regel", "regels")}, inclusief eventuele aanpassingen op basis van het huurcontract)`
                    : ""}
                  {" "}door het standaardsjabloon. Deze wijziging kan niet ongedaan worden gemaakt.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="demarcation-apply-cancel">Annuleren</AlertDialogCancel>
                <AlertDialogAction data-testid="demarcation-apply-confirm" onClick={applyTemplate}>
                  Sjabloon toepassen
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </Card>
      )}

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-body font-semibold text-foreground">Levensduur per categorie op deze locatie</h2>
        </div>
        <p className="mb-4 text-data text-muted-foreground">
          Stel hier een afwijkende levensduur in voor een categorie op deze locatie. Deze wint van de categoriestandaard, maar een per-asset instelling wint altijd.
        </p>
        {(loc.category_lifetime_overrides || []).length === 0 ? (
          <InlineEmpty
            testid="lifetime-overrides-empty"
            title="Geen afwijkende levensduur"
            purpose="Deze locatie rekent met de standaard levensduur per categorie. Wijkt hier iets af — bijvoorbeeld door gerecycled water — zet het dan hieronder."
          />
        ) : (
        <>
        <div className="hidden sm:block">
          <Table data-testid="lifetime-overrides-table">
            <TableHeader>
              <TableRow>
                <TableHead>Categorie</TableHead>
                <TableHead>Afwijkende levensduur (mnd)</TableHead>
                <TableHead>Standaard (categorie)</TableHead>
                {canWrite() && <TableHead className="w-[60px] text-right">Actie</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {(loc.category_lifetime_overrides || []).map((o) => (
                <TableRow key={o.category_id} data-testid={`override-row-${o.category_id}`}>
                  <TableCell className="text-body">{catName(o.category_id)}</TableCell>
                  <TableCell className="tabular-nums text-body font-medium">{o.lifetime_months} mnd</TableCell>
                  <TableCell className="tabular-nums text-body text-muted-foreground">{catDefault(o.category_id) != null ? `${catDefault(o.category_id)} mnd` : ""}</TableCell>
                  {canWrite() && (
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" data-testid={`override-remove-${o.category_id}`} disabled={savingOverrides} onClick={() => removeOverride(o.category_id)}><Trash2 size={18} /></Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Phone: one card per override. Four columns in 318px pushed the whole
            page — and with it the console and the strip — sideways. */}
        <div className="divide-y divide-border sm:hidden" data-testid="lifetime-overrides-cardlist">
          {(loc.category_lifetime_overrides || []).map((o) => (
            <div key={o.category_id} className="flex items-start justify-between gap-3 py-3" data-testid={`override-card-${o.category_id}`}>
              <div className="min-w-0 space-y-0.5">
                <div className="text-body font-medium text-foreground">{catName(o.category_id)}</div>
                <div className="tabular-nums text-data text-foreground">{o.lifetime_months} mnd</div>
                {catDefault(o.category_id) != null && (
                  <div className="tabular-nums text-data text-muted-foreground">standaard {catDefault(o.category_id)} mnd</div>
                )}
              </div>
              {canWrite() && (
                <Button variant="ghost" size="icon" data-testid={`override-remove-mobile-${o.category_id}`} disabled={savingOverrides} onClick={() => removeOverride(o.category_id)}><Trash2 size={18} /></Button>
              )}
            </div>
          ))}
        </div>
        </>
        )}
        {canWrite() && (
          <div className="mt-4 flex flex-wrap items-end gap-2">
            <div className="w-56">
              <Select value={newOverride.category_id} onValueChange={(v) => setNewOverride((n) => ({ ...n, category_id: v }))}>
                <SelectTrigger data-testid="override-category-select"><SelectValue placeholder="Categorie" /></SelectTrigger>
                <SelectContent>
                  {categories.filter((c) => c.active).map((c) => (
                    <SelectItem key={c.category_id} value={c.category_id}>
                      {c.name}{c.default_lifetime_months != null ? ` (standaard ${c.default_lifetime_months})` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Input type="number" min="1" className="w-40" data-testid="override-months-input" placeholder="Maanden" value={newOverride.lifetime_months} onChange={(e) => setNewOverride((n) => ({ ...n, lifetime_months: e.target.value }))} />
            <Button type="button" data-testid="override-add-button" disabled={savingOverrides} onClick={addOverride}><Plus size={18} className="mr-2" /> Toevoegen</Button>
          </div>
        )}
      </Card>

      <Card className="p-5">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-body font-semibold text-foreground">Machines op deze locatie</h2>
          <span className="text-data text-muted-foreground">{plural(assets.length, "machine", "machines")}</span>
        </div>
        {assets.length === 0 ? (
          <InlineEmpty
            testid="location-assets-empty"
            title="Nog geen machines op deze locatie"
            purpose="Zodra je hier machines vastlegt, zie je per ruimte wat er staat, hoe oud het is en wanneer het vervangen moet worden."
            action={canWrite() ? { label: "Nieuw asset", icon: Plus, onClick: () => navigate("/assets/nieuw") } : null}
          />
        ) : (
        <>
        {/* Grouped by ruimte: the answer to "wat staat er in de washal" should be
            one look, not a filter. */}
        <div className="hidden sm:block">
          <Table data-testid="location-assets-table">
            <TableHeader>
              <TableRow>
                <TableHead>Code</TableHead>
                <TableHead>Naam</TableHead>
                {sporenTotal > 0 && <TableHead>Spoor</TableHead>}
                <TableHead>Categorie</TableHead>
                <TableHead>Vervangdatum</TableHead>
                <TableHead>Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {assetGroups.map((g) => (
                <React.Fragment key={g.key}>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableCell colSpan={sporenTotal > 0 ? 6 : 5} className="text-label font-semibold uppercase tracking-[0.07em] text-muted-foreground">
                      {g.title} · {plural(g.items.length, "machine", "machines")}
                    </TableCell>
                  </TableRow>
                  {g.items.map((a) => (
                    <TableRow key={a.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/assets/${a.id}`)}>
                      <TableCell className="font-mono text-data">{a.asset_code}</TableCell>
                      <TableCell className="text-body">{a.name || `${a.brand || ""} ${a.model || ""}`.trim()}</TableCell>
                      {sporenTotal > 0 && (
                        <TableCell className="text-body text-muted-foreground">{a.spoor}</TableCell>
                      )}
                      <TableCell className="text-body">{a.category_name}</TableCell>
                      <TableCell><ReplacementBadge status={a.derived.replacement_status} date={a.derived.replacement_date} /></TableCell>
                      <TableCell><AssetStatusBadge status={a.status} /></TableCell>
                    </TableRow>
                  ))}
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </div>

        {/* Phone: one tappable card per machine, under its ruimte. */}
        <div className="sm:hidden" data-testid="location-assets-cardlist">
          {assetGroups.map((g) => (
            <div key={g.key}>
              <div className="pt-3 text-label font-semibold uppercase tracking-[0.07em] text-muted-foreground">
                {g.title}
              </div>
              <div className="divide-y divide-border">
                {g.items.map((a) => (
                  <button
                    key={a.id}
                    type="button"
                    data-testid={`location-asset-card-${a.asset_code}`}
                    onClick={() => navigate(`/assets/${a.id}`)}
                    className="flex w-full items-start justify-between gap-3 py-3 text-left"
                  >
                    <div className="min-w-0 space-y-0.5">
                      <div className="font-mono text-data text-foreground">{a.asset_code}</div>
                      <div className="truncate text-body text-foreground">{a.name || `${a.brand || ""} ${a.model || ""}`.trim()}</div>
                      <div className="text-data text-muted-foreground">{[a.spoor, a.category_name].filter(Boolean).join(" · ")}</div>
                      <div className="pt-1"><ReplacementBadge status={a.derived.replacement_status} date={a.derived.replacement_date} /></div>
                    </div>
                    <AssetStatusBadge status={a.status} />
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
        </>
        )}
      </Card>

      {canView("keuringen") && <InspectionsPanel locationId={loc.location_id} title="Keuringen op deze locatie" />}
      </div>
    </Page>
  );
}
